import { useEffect, useRef, useState } from 'react';
import { api, type Supplement, type SupplementDose } from '../lib/api';
import { Chip } from './Chip';
import { EditSupplementDrawer } from './AddSupplementModal';

// SupplementRow — single supplement entry with:
//   - name + dose summary + schedule chips
//   - cached info_label below
//   - per-pending-dose [Take]/[Skip] pair (or status chip if already acted)
//   - "?" toggle that fetches /explain on demand and caches the longer
//     coach_summary inline
//   - "⋯" menu that opens the edit drawer / soft-delete
//
// Double-click defense: setState is async, so `disabled={busy}` alone lets a
// second click within ~16ms fire a duplicate API call. We additionally gate
// every async handler on a ref so concurrent invocations no-op even before
// React has rendered the disabled state.
//
// In bulk "set times" mode the card passes `selectable` and the row grows a
// leading checkbox. Everything else about the row is unchanged — take/skip
// still work, so entering selection mode never blocks the day's actions.
export function SupplementRow({
  supplement,
  onChanged,
  selectable = false,
  selected = false,
  onSelectedChange,
}: {
  supplement: Supplement;
  onChanged: () => void;
  selectable?: boolean;
  selected?: boolean;
  onSelectedChange?: (next: boolean) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [explainOpen, setExplainOpen] = useState(false);
  const [explainLoading, setExplainLoading] = useState(false);
  const [explainText, setExplainText] = useState(supplement.coach_summary || '');
  const [showMenu, setShowMenu] = useState(false);
  const [editing, setEditing] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);

  // Synchronous re-entrancy guards — flipped instantly on click, before the
  // setState that drives the disabled UI has propagated.
  const busyRef = useRef(false);
  const explainBusyRef = useRef(false);
  const photoInputRef = useRef<HTMLInputElement | null>(null);

  // Sync the local explainText cache with the prop when the parent re-fetches.
  // This matters when the user edits the supplement's name (server clears
  // coach_summary) — without this, the row would keep showing the stale text.
  useEffect(() => {
    setExplainText(supplement.coach_summary || '');
  }, [supplement.coach_summary]);

  const scheduleSummary = supplement.schedules
    .map((s) => `${pad(s.hour)}:${pad(s.minute)}`)
    .join(' · ');

  const pendingDoses = supplement.todays_doses.filter((d) => d.status === 'pending');
  const closedDoses = supplement.todays_doses.filter((d) => d.status !== 'pending');

  const onTake = async (dose: SupplementDose) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError(null);
    try {
      await api.takeDose(dose.id);
      onChanged();
    } catch (e) {
      setError(String(e));
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };
  const onSkip = async (dose: SupplementDose) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError(null);
    try {
      await api.skipDose(dose.id);
      onChanged();
    } catch (e) {
      setError(String(e));
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const onExplain = async () => {
    // Toggle close.
    if (explainOpen) {
      setExplainOpen(false);
      return;
    }
    setExplainOpen(true);
    if (explainText) return; // cached client-side
    if (explainBusyRef.current) return; // de-dupe rapid clicks
    explainBusyRef.current = true;
    setExplainLoading(true);
    try {
      const r = await api.explainSupplement(supplement.id);
      setExplainText(r.coach_summary || r.info_label);
    } catch (e) {
      setExplainText(`(coach unavailable: ${String(e)})`);
    } finally {
      explainBusyRef.current = false;
      setExplainLoading(false);
    }
  };

  const onAttachPhoto = async (file: File) => {
    if (photoUploading) return;
    setError(null);
    setPhotoUploading(true);
    try {
      await api.attachSupplementPhoto(supplement.id, file);
      onChanged();
    } catch (e) {
      setError(`Couldn't attach photo: ${String(e)}`);
    } finally {
      setPhotoUploading(false);
    }
  };

  const onDelete = async () => {
    if (busyRef.current) return;
    if (!confirm(`Stop tracking "${supplement.name}"? Past logs are kept; future reminders stop.`)) {
      return;
    }
    busyRef.current = true;
    setBusy(true);
    try {
      await api.deleteSupplement(supplement.id);
      onChanged();
    } catch (e) {
      setError(String(e));
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  return (
    // A row inside a white card gets a hairline, not a second grey box.
    <div className="rounded-panel border border-line p-3 space-y-2">
      <div className="flex items-start justify-between gap-2">
        {selectable && (
          // Same checkbox material as Card.tsx's Toggle: solid ink when
          // checked, an empty sunken well when not. The p-2 wrapper takes the
          // 20px box to a 36px tap target.
          <label className="flex items-start shrink-0 p-2 -m-1 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={selected}
              onChange={(e) => onSelectedChange?.(e.target.checked)}
              aria-label={`Select ${supplement.name}`}
              className="sr-only peer"
            />
            <span
              aria-hidden
              className={`grid place-items-center size-5 rounded-[7px] border transition-all ${
                selected
                  ? 'bg-accent border-accent text-accent-fg'
                  : 'bg-bg-sunken border-line-strong text-transparent'
              }`}
            >
              <svg
                viewBox="0 0 16 16"
                className="size-3.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M3 8.5l3.5 3.5L13 5" />
              </svg>
            </span>
          </label>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2 mb-0.5">
            <h3 className="text-sm font-bold tracking-[-0.01em] text-fg truncate">
              {supplement.name}
            </h3>
            <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-fg-faint shrink-0">
              {supplement.dose}
            </span>
          </div>
          <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-fg-faint">
            {scheduleSummary || 'No schedule'}
          </p>
          {supplement.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {supplement.tags.map((t) => (
                <Chip key={t} tone="muted">
                  {t}
                </Chip>
              ))}
            </div>
          )}
          {supplement.info_label && (
            <p className="text-xs text-fg-muted leading-snug mt-1.5">{supplement.info_label}</p>
          )}
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <button
            type="button"
            onClick={onExplain}
            disabled={explainLoading}
            aria-label="Why this supplement"
            aria-pressed={explainOpen}
            className="size-9 rounded-full border border-line-strong text-fg-muted hover:text-fg hover:bg-bg-sunken text-sm leading-none transition-colors disabled:opacity-50"
          >
            ?
          </button>
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowMenu((s) => !s)}
              aria-label="Supplement options"
              aria-expanded={showMenu}
              className="size-9 rounded-full border border-line-strong text-fg-muted hover:text-fg hover:bg-bg-sunken text-sm leading-none transition-colors"
            >
              ⋯
            </button>
            {showMenu && (
              <div
                role="menu"
                className="absolute right-0 top-full mt-1 z-10 rounded-panel border border-line bg-bg-raised shadow-[0_1px_2px_rgba(11,11,15,0.05),0_16px_40px_-16px_rgba(11,11,15,0.26)] overflow-hidden min-w-[120px]"
                onMouseLeave={() => setShowMenu(false)}
              >
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setShowMenu(false);
                    setEditing(true);
                  }}
                  className="block w-full text-left px-3 py-2.5 text-xs text-fg hover:bg-fg/[0.04] transition-colors"
                >
                  Edit
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setShowMenu(false);
                    void onDelete();
                  }}
                  className="block w-full text-left px-3 py-2.5 text-xs text-danger hover:bg-danger-soft transition-colors"
                >
                  Delete
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {explainOpen && (
        <div
          role="region"
          aria-label="Coach explanation"
          className="rounded-panel border border-line bg-bg-raised-2 p-2.5 space-y-2"
        >
          {supplement.photo_url && (
            <img
              src={supplement.photo_url}
              alt={`${supplement.name} bottle`}
              className="w-full max-h-48 object-contain rounded-control bg-bg-sunken border border-line"
              loading="lazy"
            />
          )}
          <div className="text-xs text-fg-muted leading-relaxed">
            {explainLoading ? (
              <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-fg-faint">
                Asking the coach…
              </span>
            ) : explainText ? (
              explainText
            ) : (
              <span className="text-fg-faint">No explanation available.</span>
            )}
          </div>
          <input
            ref={photoInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            capture="environment"
            className="sr-only"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void onAttachPhoto(f);
              e.target.value = '';
            }}
          />
          <button
            type="button"
            onClick={() => photoInputRef.current?.click()}
            disabled={photoUploading}
            className="font-mono text-[10px] uppercase tracking-[0.14em] text-fg-faint hover:text-fg transition-colors disabled:opacity-50"
          >
            {photoUploading
              ? 'Uploading…'
              : supplement.photo_url
                ? 'Replace photo'
                : 'Attach photo'}
          </button>
        </div>
      )}

      {pendingDoses.length > 0 && (
        <div className="space-y-1.5">
          {pendingDoses.map((dose) => (
            <div key={dose.id} className="flex items-center gap-1.5">
              <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-fg-faint w-12 shrink-0">
                {dose.scheduled_time}
              </span>
              <button
                type="button"
                onClick={() => onTake(dose)}
                disabled={busy}
                className="btn-primary flex-1 py-2.5 text-xs"
              >
                ✓ Take
              </button>
              <button
                type="button"
                onClick={() => onSkip(dose)}
                disabled={busy}
                className="btn-secondary flex-1 py-2.5 text-xs"
              >
                Skip
              </button>
            </div>
          ))}
        </div>
      )}

      {closedDoses.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {closedDoses.map((dose) => (
            <Chip key={dose.id} tone={dose.status === 'taken' ? 'good' : 'muted'} dot>
              {dose.scheduled_time} · {dose.status}
            </Chip>
          ))}
        </div>
      )}

      {error && (
        <pre className="text-xs text-danger whitespace-pre-wrap break-all">{error}</pre>
      )}

      <EditSupplementDrawer
        open={editing}
        supplement={supplement}
        onClose={() => setEditing(false)}
        onSaved={() => {
          setEditing(false);
          onChanged();
        }}
      />
    </div>
  );
}

// pad zero-pads a 0-23 hour or 0-59 minute to two digits.
function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}
